import { useState, useEffect } from 'react';
import API from '../utils/API';
import './billTrackingModal.css';

const POSITIONS = [
    'Support',
    'Support if amended',
    'Neutral',
    'Neutral with concerns',
    'Neutral if amended',
    'Oppose',
    'Oppose unless amended'
];

const PRIORITIES = ['Critical', 'High', 'Standard', 'Low', 'Watch'];

const BLANK_FORM = { position: '', priority: '', notes: '' };

export default function BillTrackingModal({ bill, billVersionId, userId, initialTracker, onClose, onSaved }) {
    const [modalData, setModalData] = useState(null);
    const [loadingData, setLoadingData] = useState(true);
    const [selectedTracker, setSelectedTracker] = useState(initialTracker ?? 'personal');
    const [form, setForm] = useState(BLANK_FORM);

    // Per-client rows for team trackers
    const [clientRows, setClientRows] = useState([]);

    // Add single client state
    const [showAddClient, setShowAddClient] = useState(false);
    const [addClientId, setAddClientId] = useState('');
    const [addClientForm, setAddClientForm] = useState(BLANK_FORM);

    // Batch add state
    const [batchMode, setBatchMode] = useState(false);
    const [batchSelected, setBatchSelected] = useState([]);
    const [batchForm, setBatchForm] = useState(BLANK_FORM);

    const [saving, setSaving] = useState(false);

    // Load modal data on mount
    useEffect(() => {
        API.getBillTrackingModal(bill.bill_id, userId)
            .then(res => setModalData(res.data))
            .catch(err => console.error("Error loading tracking modal:", err))
            .finally(() => setLoadingData(false));
    }, [bill.bill_id, userId]);

    // Re-populate form whenever tracker or modalData changes
    useEffect(() => {
        if (!modalData) return;
        if (selectedTracker === 'personal') {
            const p = modalData.personal;
            setForm({ position: p.position || '', priority: p.priority || '', notes: p.notes || '' });
            setClientRows([]);
        } else {
            const teamData = modalData.teams.find(t => t.team_id === selectedTracker);
            if (teamData) {
                setForm({ position: teamData.position || '', priority: teamData.priority || '', notes: teamData.notes || '' });
                setClientRows(
                    teamData.clients
                        .filter(c => c.active_track)
                        .map(c => ({
                            client_id: c.client_id,
                            client_name: c.client_name,
                            position: c.position || '',
                            priority: c.priority || '',
                            notes: c.notes || '',
                        }))
                );
            }
        }
        setShowAddClient(false);
        setAddClientId('');
        setAddClientForm(BLANK_FORM);
        setBatchMode(false);
        setBatchSelected([]);
        setBatchForm(BLANK_FORM);
    }, [selectedTracker, modalData]);

    const currentTeam = selectedTracker !== 'personal'
        ? modalData?.teams.find(t => t.team_id === selectedTracker)
        : null;

    const showClientsSection = currentTeam?.clients?.length > 0;

    // Clients not yet in clientRows
    const untrackedClients = currentTeam?.clients?.filter(
        c => !clientRows.find(r => r.client_id === c.client_id)
    ) || [];

    // ── Handlers ──────────────────────────────────────────────

    const handleTrackerChange = (val) => {
        setSelectedTracker(val === 'personal' ? 'personal' : Number(val));
    };

    const handleAddSingleClient = () => {
        if (!addClientId) return;
        const client = currentTeam.clients.find(c => c.client_id === Number(addClientId));
        if (!client) return;
        setClientRows(prev => [
            ...prev,
            { client_id: client.client_id, client_name: client.client_name, ...addClientForm },
        ]);
        setAddClientId('');
        setAddClientForm(BLANK_FORM);
        setShowAddClient(false);
    };

    const handleRemoveClientRow = (clientId) => {
        setClientRows(prev => prev.filter(r => r.client_id !== clientId));
    };

    const handleClientFieldChange = (clientId, field, value) => {
        setClientRows(prev =>
            prev.map(r => r.client_id === clientId ? { ...r, [field]: value } : r)
        );
    };

    const handleBatchApply = () => {
        if (batchSelected.length === 0) return;
        const newRows = batchSelected.map(clientId => {
            const client = currentTeam.clients.find(c => c.client_id === clientId);
            return { client_id: client.client_id, client_name: client.client_name, ...batchForm };
        });
        setClientRows(prev => [
            ...prev.filter(r => !batchSelected.includes(r.client_id)),
            ...newRows,
        ]);
        setBatchSelected([]);
        setBatchForm(BLANK_FORM);
        setBatchMode(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                user_id: userId,
                bill_id: bill.bill_id,
                bill_version_id: billVersionId,
                active_track: true,
                position: form.position || null,
                priority: form.priority || null,
                notes: form.notes || null,
            };

            if (selectedTracker === 'personal') {
                payload.tracker_type = 'personal';
            } else {
                payload.tracker_type = 'team';
                payload.team_id = selectedTracker;
                payload.client_trackings = clientRows.map(r => ({
                    client_id: r.client_id,
                    active_track: true,
                    position: r.position || null,
                    priority: r.priority || null,
                    notes: r.notes || null,
                }));
            }

            await API.saveBillTracking(payload);
            onSaved?.();
            onClose();
        } catch (err) {
            console.error("Error saving tracking:", err);
        } finally {
            setSaving(false);
        }
    };

    // ── Shared sub-forms ───────────────────────────────────────

    const PositionSelect = ({ value, onChange, placeholder = '— Position —' }) => (
        <select className="form-select form-select-sm" value={value} onChange={e => onChange(e.target.value)}>
            <option value="">{placeholder}</option>
            {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
    );

    const PrioritySelect = ({ value, onChange, placeholder = '— Priority —' }) => (
        <select className="form-select form-select-sm" value={value} onChange={e => onChange(e.target.value)}>
            <option value="">{placeholder}</option>
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
    );

    // ── Render ─────────────────────────────────────────────────

    return (
        <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
            onClick={onClose}
        >
            <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable btp-dialog"
                onClick={e => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            Track: {bill.bill_type} {bill.bill_number}
                        </h5>
                    </div>

                    {loadingData ? (
                        <div className="modal-body text-center py-5">
                            <div className="spinner-border spinner-border-sm text-secondary" />
                        </div>
                    ) : (
                        <div className="modal-body btp-body">
                            {/* Tracker selector */}
                            <div className="mb-3">
                                <label className="btp-label">Tracking for</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={selectedTracker === 'personal' ? 'personal' : selectedTracker}
                                    onChange={e => handleTrackerChange(e.target.value)}
                                >
                                    <option value="personal">My Tracking</option>
                                    {modalData?.teams.filter(t => t.bill_manage_permission).map(t => (
                                        <option key={t.team_id} value={t.team_id}>{t.team_name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Position & Priority */}
                            <div className="row g-2 mb-3">
                                <div className="col-6">
                                    <label className="btp-label">Position</label>
                                    <PositionSelect
                                        value={form.position}
                                        onChange={v => setForm(f => ({ ...f, position: v }))}
                                        placeholder="— None —"
                                    />
                                </div>
                                <div className="col-6">
                                    <label className="btp-label">Priority</label>
                                    <PrioritySelect
                                        value={form.priority}
                                        onChange={v => setForm(f => ({ ...f, priority: v }))}
                                        placeholder="— None —"
                                    />
                                </div>
                            </div>

                            {/* Notes */}
                            <div className="mb-1">
                                <label className="btp-label">Notes</label>
                                <textarea
                                    className="form-control form-control-sm btp-notes"
                                    rows={3}
                                    placeholder="Notes on this bill…"
                                    value={form.notes}
                                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                                />
                            </div>

                            {/* ── Client / Stakeholder Group positions ── */}
                            {showClientsSection && (
                                <div className="btp-clients-section">
                                    <div className="d-flex align-items-center justify-content-between btp-clients-header">
                                        <span className="btp-section-label">{currentTeam.clientsLabel} Positions</span>
                                        {!batchMode && !showAddClient && (
                                            <div className="d-flex gap-2">
                                                {untrackedClients.length > 0 && (
                                                    <button
                                                        className="btn btp-btn-sm"
                                                        onClick={() => setShowAddClient(true)}
                                                    >
                                                        <i className="bi bi-plus me-1" />Add {currentTeam.singleClientLabel}
                                                    </button>
                                                )}
                                                {untrackedClients.length >= 2 && (
                                                    <button
                                                        className="btn btp-btn-sm"
                                                        onClick={() => setBatchMode(true)}
                                                    >
                                                        <i className="bi bi-collection me-1" />Batch Add
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Existing tracked client rows */}
                                    {clientRows.length > 0 && (
                                        <div className="btp-client-list">
                                            {clientRows.map(row => (
                                                <div key={row.client_id} className="btp-client-row">
                                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                                        <span className="btp-client-name">{row.client_name}</span>
                                                        <i
                                                            className="bi bi-x btp-remove-icon"
                                                            title="Remove"
                                                            onClick={() => handleRemoveClientRow(row.client_id)}
                                                        />
                                                    </div>
                                                    <div className="row g-1">
                                                        <div className="col-6">
                                                            <PositionSelect
                                                                value={row.position}
                                                                onChange={v => handleClientFieldChange(row.client_id, 'position', v)}
                                                            />
                                                        </div>
                                                        <div className="col-6">
                                                            <PrioritySelect
                                                                value={row.priority}
                                                                onChange={v => handleClientFieldChange(row.client_id, 'priority', v)}
                                                            />
                                                        </div>
                                                        <div className="col-12">
                                                            <textarea
                                                                className="form-control form-control-sm"
                                                                placeholder="Notes"
                                                                value={row.notes}
                                                                onChange={e => handleClientFieldChange(row.client_id, 'notes', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add single client / group */}
                                    {showAddClient && !batchMode && (
                                        <div className="btp-inline-form">
                                            <select
                                                className="form-select form-select-sm mb-2"
                                                value={addClientId}
                                                onChange={e => setAddClientId(e.target.value)}
                                            >
                                                <option value="">Select {currentTeam.singleClientLabel?.toLowerCase()}…</option>
                                                {untrackedClients.map(c => (
                                                    <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                                                ))}
                                            </select>
                                            <div className="row g-1 mb-2">
                                                <div className="col-6">
                                                    <PositionSelect
                                                        value={addClientForm.position}
                                                        onChange={v => setAddClientForm(f => ({ ...f, position: v }))}
                                                    />
                                                </div>
                                                <div className="col-6">
                                                    <PrioritySelect
                                                        value={addClientForm.priority}
                                                        onChange={v => setAddClientForm(f => ({ ...f, priority: v }))}
                                                    />
                                                </div>
                                                <div className="col-12">
                                                    <input
                                                        className="form-control form-control-sm"
                                                        placeholder="Notes"
                                                        value={addClientForm.notes}
                                                        onChange={e => setAddClientForm(f => ({ ...f, notes: e.target.value }))}
                                                    />
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-sm btp-add-btn"
                                                    onClick={handleAddSingleClient}
                                                    disabled={!addClientId}
                                                >
                                                    Add
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => { setShowAddClient(false); setAddClientId(''); setAddClientForm(BLANK_FORM); }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Batch add */}
                                    {batchMode && (
                                        <div className="btp-inline-form">
                                            <p className="btp-label mb-2">Select {currentTeam.clientsLabel?.toLowerCase()} to apply the same position:</p>
                                            <div className="btp-batch-list mb-2">
                                                {untrackedClients.map(c => (
                                                    <label key={c.client_id} className="btp-batch-check">
                                                        <input
                                                            type="checkbox"
                                                            checked={batchSelected.includes(c.client_id)}
                                                            onChange={e => {
                                                                if (e.target.checked) {
                                                                    setBatchSelected(prev => [...prev, c.client_id]);
                                                                } else {
                                                                    setBatchSelected(prev => prev.filter(id => id !== c.client_id));
                                                                }
                                                            }}
                                                        />
                                                        <span>{c.client_name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                            <div className="row g-1 mb-2">
                                                <div className="col-6">
                                                    <PositionSelect
                                                        value={batchForm.position}
                                                        onChange={v => setBatchForm(f => ({ ...f, position: v }))}
                                                    />
                                                </div>
                                                <div className="col-6">
                                                    <PrioritySelect
                                                        value={batchForm.priority}
                                                        onChange={v => setBatchForm(f => ({ ...f, priority: v }))}
                                                    />
                                                </div>
                                                <div className="col-12">
                                                    <input
                                                        className="form-control form-control-sm"
                                                        placeholder="Notes (applied to all selected)"
                                                        value={batchForm.notes}
                                                        onChange={e => setBatchForm(f => ({ ...f, notes: e.target.value }))}
                                                    />
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-sm btp-add-btn"
                                                    onClick={handleBatchApply}
                                                    disabled={batchSelected.length === 0}
                                                >
                                                    Apply to {batchSelected.length} {batchSelected.length !== 1 ? currentTeam.clientsLabel?.toLowerCase() : currentTeam.singleClientLabel?.toLowerCase()}
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => { setBatchMode(false); setBatchSelected([]); setBatchForm(BLANK_FORM); }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {clientRows.length === 0 && !showAddClient && !batchMode && (
                                        <p className="text-muted small fst-italic mb-0 mt-1">No {currentTeam.clientsLabel?.toLowerCase()} positions added yet.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="modal-footer btp-footer">
                        <button className="btn btp-close-btn" onClick={onClose}>Cancel</button>
                        <button
                            className="btn btp-save-btn"
                            onClick={handleSave}
                            disabled={saving || loadingData}
                        >
                            {saving ? 'Saving…' : 'Save changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
